import React, { useState, useEffect } from 'react';
import { School, User } from 'lucide-react';
import { auth, db } from '../firebase';
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, signOut,
         sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useAuthUser } from '../authState';
import { useT, LANGUAGES } from '../i18n.jsx';
import { useNavigate } from 'react-router-dom';
import { tenantFromLocation } from '../tenant';
import { lookupTenant } from '../tenantLookup';
import { applySchoolBranding } from '../pwa';

const Login = ({ isDeveloperLogin = false }) => {
  const { t, language, setLanguage } = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState(null);
  const [tenantDomain, setTenantDomain] = useState(null);
  // The id behind that label, kept from the lookup the branding already did.
  const [brandingSchoolId, setBrandingSchoolId] = useState('');
  // Guardians sign in with a link sent to their address instead of a password. They never had one to
  // begin with -- their account is created by their child being enrolled, not by an administrator
  // handing out a credential -- so there is nothing to distribute and nothing to reset.
  const [parentMode, setParentMode] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  const PENDING_KEY = 'parentSignInEmail';
  const navigate = useNavigate();

  // The login screen should never be the resting place of a live session. RootRoute is supposed to
  // prevent that, but "supposed to" is what the last three attempts each relied on -- so the screen
  // that is actually showing checks for itself. Anything that lands here with a session, by any
  // route, leaves.
  //
  // Not while finishing an email link: that flow legitimately signs in and then navigates itself.
  const settledUser = useAuthUser();
  const finishingEmailLink = isSignInWithEmailLink(auth, window.location.href);
  useEffect(() => {
    if (settledUser && !finishingEmailLink && !isDeveloperLogin) navigate('/dashboard', { replace: true });
  }, [settledUser, finishingEmailLink, isDeveloperLogin, navigate]);

  React.useEffect(() => {
    const domain = tenantFromLocation();
    setTenantDomain(domain);
    
    if (isDeveloperLogin) {
      setBranding({
        name: t('login.developerName'),
        tagline: t('login.developerTagline')
      });
      return;
    }
    
    if (domain) {
      // One public document, read by id. This used to query the whole schools collection by domain,
      // which meant every school had to be publicly readable -- and a collection anybody may list is
      // a directory of every customer. See src/tenantLookup.js.
      lookupTenant(domain).then((school) => {
        if (school) {
          const brandingData = {
            tagline: t('login.welcome', { name: school.name }),
            name: school.name,
            logoUrl: school.logoUrl || '',
            primaryColor: school.primaryColor || '',
          };

          setBranding(brandingData);
          setBrandingSchoolId(school.schoolId || '');
          // The same call Layout makes after sign-in, made here as well because this is where an
          // installed app is usually born: somebody is shown the portal, taps Add to Home Screen
          // from the login page, and never reaches Layout at all. Branding only after sign-in put
          // the platform's name and badge on that icon -- on the one screen a principal is most
          // likely to be looking at.
          applySchoolBranding({
            name: brandingData.name || '',
            logoUrl: brandingData.logoUrl || '',
            themeColor: brandingData.primaryColor || '',
          });
        }
      });
    } else {
      document.documentElement.style.setProperty('--color-primary', 'var(--color-info)');
    }
  }, []);

  // Authenticating with Firebase only proves "this is a real Google/password account" -- it says
  // nothing about whether the person belongs to the school they are signing in to. Google sign-in in
  // particular accepts ANY Google account, so without this check a stranger could obtain a valid
  // session. Firestore rules are the real boundary (they scope every read to school membership), but
  // holding a session that can't do anything is confusing, so drop it here and say why.
  const verifyMembership = async (userEmail) => {
    if (!tenantDomain) return { ok: true };

    // Resolved above, when the screen branded itself. Re-resolving here would be a second read of
    // the same document to learn the same thing.
    let schoolId = brandingSchoolId;
    if (!schoolId) {
      const school = await lookupTenant(tenantDomain);
      // Nothing could be reached. That is this screen's problem, not the person's -- and this check
      // is a courtesy (a clearer message than a portal full of denials), never the boundary.
      // Refusing here would mean one missing derived document logs out every member of the school,
      // which is exactly what happened once. Stand aside and let the rules do their job.
      if (school === undefined) return { ok: true };
      // Told, rather than unable to ask: this address names no school, and saying so is kinder than
      // signing somebody into an empty portal.
      if (!school?.schoolId) return { ok: false, reason: t('login.unknownSchool') };
      schoolId = school.schoolId;
    }

    const accountId = userEmail.toLowerCase();
    // A member may be recorded in any one of these; a non-member matches none. Reads that throw are
    // treated as "not found" -- the rules deny the read precisely when the caller isn't a member.
    for (const col of ['staff_profiles', 'student_profiles', 'user_permissions']) {
      try {
        const d = await getDoc(doc(db, 'schools', schoolId, col, accountId));
        if (d.exists()) return { ok: true };
      } catch {
        // permission denied -- keep checking the remaining collections
      }
    }
    return { ok: false, reason: t('login.notRegistered') };
  };

  const rejectNonMember = async (signedInUser) => {
    const result = await verifyMembership(signedInUser.email);
    if (!result.ok) {
      await signOut(auth);
      setError(result.reason);
      return true;
    }

    // Force a token refresh so the schoolId/role custom claims are on the token before the app
    // issues its first Firestore query. Without this a freshly provisioned account would run its
    // first session on a claim-less token and fall back to the rules' document lookups.
    try {
      await signedInUser.getIdToken(true);
    } catch {
      // Non-fatal: the rules' document fallback still authorises this session correctly.
    }
    return false;
  };

  // Completing the link. The address has to be confirmed on this device: the link alone is a bearer
  // token, so Firebase requires the email back to stop a forwarded link signing somebody else in.
  useEffect(() => {
    if (isDeveloperLogin) return;
    if (!isSignInWithEmailLink(auth, window.location.href)) return;

    const stored = window.localStorage.getItem(PENDING_KEY);
    const address = stored || window.prompt(t('login.confirmEmail'));
    if (!address) {
      setError(t('login.emailNeededToFinish'));
      return;
    }

    setLoading(true);
    (async () => {
      try {
        const userCred = await signInWithEmailLink(auth, address, window.location.href);
        window.localStorage.removeItem(PENDING_KEY);
        if (await rejectNonMember(userCred.user)) return;
        navigate(tenantDomain && isLocalhost() ? `/dashboard?tenant=${tenantDomain}` : '/dashboard');
      } catch (err) {
        setError(err.code === 'auth/invalid-action-code'
          ? t('login.linkUsed')
          : err.message.replace(/^Firebase:\s*/, '').trim());
      } finally {
        setLoading(false);
      }
    })();
    // Runs once the tenant is known, so the redirect below lands on the right school.
  }, [tenantDomain, isDeveloperLogin]);

  const isLocalhost = () =>
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  const handleSendParentLink = async (e) => {
    e.preventDefault();
    setError('');
    const address = email.trim().toLowerCase();
    if (!address.includes('@')) { setError(t('login.needEmail')); return; }

    setLoading(true);
    try {
      // Back to this same subdomain, so a guardian with children at two schools lands at the one
      // whose login page they used rather than having to choose.
      await sendSignInLinkToEmail(auth, address, {
        url: `${window.location.origin}/login`,
        handleCodeInApp: true,
      });
      window.localStorage.setItem(PENDING_KEY, address);
      setLinkSent(true);
    } catch (err) {
      console.error(err);
      // Two of these are OUR configuration, not the visitor's address, and reporting them plainly
      // costs nothing: neither reveals whether an address is registered. Showing the vague message
      // for them made a missing console setting look like a typo in the email.
      if (err.code === 'auth/operation-not-allowed') {
        setError(t('login.passwordlessOff'));
      } else if (err.code === 'auth/unauthorized-continue-uri'
                 || err.code === 'auth/invalid-continue-uri') {
        setError(t('login.domainNotAuthorised', { host: window.location.hostname }));
      } else {
        // Everything else stays vague on purpose. Saying "no such account" would let anyone probe
        // the school's parent roster one address at a time.
        setError(t('login.linkFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const userCred = await signInWithEmailAndPassword(auth, email, password);
      if (isDeveloperLogin) {
        let isDeveloper = false;
        try {
          const devSnap = await getDoc(doc(db, 'developers', userCred.user.email));
          if (devSnap.exists()) isDeveloper = true;
        } catch (err) {}
        
        if (isDeveloper) {
          navigate('/developer');
        } else {
          setError(t('login.noDeveloperAccess'));
          // Don't auto logout yet, just show error
        }
      } else {
        if (await rejectNonMember(userCred.user)) return;
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        let baseDomain = window.location.hostname;
        if (baseDomain.startsWith('www.')) baseDomain = baseDomain.substring(4);
        
        if (tenantDomain && !isLocalhost && !baseDomain.startsWith(tenantDomain + '.')) {
          window.location.href = `https://${tenantDomain}.${baseDomain}/dashboard`;
        } else if (tenantDomain && isLocalhost) {
          navigate(`/dashboard?tenant=${tenantDomain}`);
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      if (err.code === 'auth/invalid-credential') {
        setError(t('login.invalidCredential'));
      } else {
        setError(err.message.replace(/^Firebase:\s*/, '').replace(/\(auth\/[^)]+\)\.?$/, '').trim());
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    const provider = new GoogleAuthProvider();
    try {
      const userCred = await signInWithPopup(auth, provider);
      if (isDeveloperLogin) {
        let isDeveloper = false;
        try {
          const devSnap = await getDoc(doc(db, 'developers', userCred.user.email));
          if (devSnap.exists()) isDeveloper = true;
        } catch (err) {}
        
        if (isDeveloper) {
          navigate('/developer');
        } else {
          setError(t('login.noDeveloperAccess'));
        }
      } else {
        if (await rejectNonMember(userCred.user)) return;
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        let baseDomain = window.location.hostname;
        if (baseDomain.startsWith('www.')) baseDomain = baseDomain.substring(4);
        
        if (tenantDomain && !isLocalhost && !baseDomain.startsWith(tenantDomain + '.')) {
          window.location.href = `https://${tenantDomain}.${baseDomain}/dashboard`;
        } else if (tenantDomain && isLocalhost) {
          navigate(`/dashboard?tenant=${tenantDomain}`);
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      if (err.code === 'auth/invalid-credential') {
        setError(t('login.invalidCredential'));
      } else {
        setError(err.message.replace(/^Firebase:\s*/, '').replace(/\(auth\/[^)]+\)\.?$/, '').trim());
      }
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '1rem' }}>
      <div style={{ 
        backgroundColor: 'var(--color-bg-card)', 
        borderRadius: 'var(--radius-lg)', 
        padding: '2.5rem 2rem', 
        width: '100%', 
        maxWidth: '420px',
        boxShadow: 'var(--shadow-lg)'
      }} className="animate-fade-in">
        
        {/* Header Section */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          {branding?.logoUrl ? (
            <img 
              src={branding.logoUrl} 
              alt={t('login.logoAlt')} 
              style={{ maxHeight: '80px', maxWidth: '100%', objectFit: 'contain', margin: '0 auto 1.5rem', display: 'block' }} 
            />
          ) : (
            <div style={{ 
              backgroundColor: 'var(--color-primary)', 
              width: '64px', height: '64px', 
              borderRadius: '16px', 
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 1.5rem',
              color: 'white'
            }}>
              <School size={36} />
            </div>
          )}
          <h1 style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '0.5rem' }}>
            {branding?.name || t('login.fallbackName')}
          </h1>
          <p style={{ color: 'var(--color-text-muted)' }}>
            {branding?.tagline || t('login.fallbackTagline')}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{ 
            backgroundColor: 'var(--color-danger-bg)', 
            color: 'var(--color-danger)', 
            padding: '0.75rem', 
            borderRadius: 'var(--radius-md)', 
            fontSize: '0.875rem',
            marginBottom: '1.5rem',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        {/* Login Form */}
        {parentMode && !isDeveloperLogin ? (
          linkSent ? (
            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
              <p style={{ fontSize: '0.95rem', margin: 0, lineHeight: 1.7 }}>
                {t('login.linkSentLead')}<strong>{email.trim().toLowerCase()}</strong>{t('login.linkSentEnd')}
              </p>
              <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', margin: 0, lineHeight: 1.7 }}>
                {t('login.linkOnce')}
              </p>
              <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', margin: 0, lineHeight: 1.7 }}>
                <strong>{t('login.notArrivedLead')}</strong> {t('login.notArrived')}
              </p>
              <button
                type="button"
                onClick={() => { setLinkSent(false); setError(''); }}
                style={{ background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', fontSize: '0.85rem' }}
              >
                {t('login.differentAddress')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSendParentLink}>
              <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', margin: '0 0 1.25rem', lineHeight: 1.7 }}>
                {t('login.parentIntro')}
              </p>
              <div style={{ marginBottom: '1.25rem' }}>
                <label className="input-label">{t('login.emailAddress')}</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field"
                  placeholder={t('login.emailPlaceholder')}
                  required
                  autoComplete="email"
                />
              </div>
              <button type="submit" className="btn-primary" disabled={loading} style={{ width: '100%' }}>
                {loading ? t('login.sending') : t('login.sendLink')}
              </button>
            </form>
          )
        ) : (
        <form onSubmit={handleEmailLogin}>
          <div style={{ marginBottom: '1.25rem' }}>
            <label className="input-label">{t('login.emailAddress')}</label>
            <div style={{ position: 'relative' }}>
              <input 
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field"
                style={{ paddingRight: '2.5rem' }}
                required
              />
              <User size={18} style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }} />
            </div>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label className="input-label">{t('login.password')}</label>
            <input 
              type="password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field"
              required
            />
          </div>

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? t('login.signingIn') : t('login.signIn')}
          </button>
        </form>
        )}

        {!isDeveloperLogin && (
          <button
            type="button"
            onClick={() => { setParentMode(!parentMode); setError(''); setLinkSent(false); }}
            style={{ background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', fontSize: '0.85rem', marginTop: '1.25rem', width: '100%', textAlign: 'center' }}
          >
            {parentMode ? t('login.staffPrompt') : t('login.parentPrompt')}
          </button>
        )}
        <div style={{ margin: '1.5rem 0', textAlign: 'center', position: 'relative' }}>
          <hr style={{ border: 'none', borderTop: '1px solid var(--color-border)' }} />
          <span style={{ 
            position: 'absolute', 
            top: '50%', left: '50%', 
            transform: 'translate(-50%, -50%)', 
            backgroundColor: 'var(--color-bg-card)', 
            padding: '0 0.75rem',
            color: 'var(--color-text-muted)',
            fontSize: '0.875rem'
          }}>
            {t('login.or')}
          </span>
        </div>

        <button onClick={handleGoogleLogin} className="btn-outline">
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" style={{ width: '18px', height: '18px' }} />
          {t('login.googleSignIn')}
        </button>


        <div style={{ display: 'flex', gap: '0.35rem', justifyContent: 'center', marginTop: '1.5rem' }}>
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => setLanguage(l.code)}
              aria-pressed={language === l.code}
              style={{
                padding: '0.3rem 0.7rem',
                fontSize: '0.78rem',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                fontWeight: language === l.code ? '700' : '500',
                backgroundColor: 'transparent',
                border: `1px solid ${language === l.code ? 'var(--color-primary)' : 'var(--color-border)'}`,
                color: language === l.code ? 'var(--color-primary)' : 'var(--color-text-muted)',
              }}
            >
              {l.label}
            </button>
          ))}
        </div>

      </div>
    </div>
  );
};

export default Login;
